import { XIcon } from "lucide-react";
import React from "react";
import type {
  TagGroupProps as TagGroupPrimitiveProps,
  TagListProps,
  TagProps as TagPrimitiveProps,
} from "react-aria-components";
import {
  Button,
  composeRenderProps,
  TagGroup as TagGroupPrimitive,
  TagList as TagListPrimitive,
  Tag as TagPrimitive,
} from "react-aria-components";
import { tv, type VariantProps } from "tailwind-variants";

import { badgeStyles } from "@/components/ui/badge";
import { Description, Label } from "@/components/ui/field";
import { cn, composeTailwindRenderProps } from "@/components/ui/utils";

export type RestrictedIntent = "primary" | "secondary";

const tagStyles = tv({
  extend: badgeStyles,
  defaultVariants: {
    color: "gray",
    shape: "square",
    variant: "soft",
    size: "md",
  },
});

type TagGroupContextValue = VariantProps<typeof tagStyles>;

const TagGroupContext = React.createContext<TagGroupContextValue>({
  color: "gray",
  shape: "square",
  variant: "soft",
  size: "md",
});

export type TagGroupProps = TagGroupPrimitiveProps &
  VariantProps<typeof tagStyles> & {
    errorMessage?: string;
    label?: string;
    description?: string;
  };

export function TagGroup({ children, ...props }: TagGroupProps) {
  return (
    <TagGroupPrimitive
      {...props}
      className={cn("flex flex-wrap flex-col gap-1", props.className)}
    >
      <TagGroupContext.Provider
        value={{
          color: props.color || "gray",
          shape: props.shape || "square",
          variant: props.variant || "soft",
          size: props.size || "md",
        }}
      >
        {props.label && <Label>{props.label}</Label>}
        {children}
        {props.description && <Description>{props.description}</Description>}
      </TagGroupContext.Provider>
    </TagGroupPrimitive>
  );
}

export function TagList<T extends object>({
  className,
  ...props
}: TagListProps<T>) {
  return (
    <TagListPrimitive
      {...props}
      className={composeTailwindRenderProps(className, "flex flex-wrap gap-2")}
    />
  );
}

type TagProps = TagPrimitiveProps & VariantProps<typeof tagStyles>;

export function Tag({
  children,
  className,
  color,
  shape,
  variant,
  size,
  ...props
}: TagProps) {
  const textValue = typeof children === "string" ? children : undefined;
  const groupContext = React.useContext(TagGroupContext);

  return (
    <TagPrimitive
      textValue={textValue}
      {...props}
      className={composeRenderProps(className, (_, renderProps) => {
        const finalColor = color || groupContext.color;
        const finalShape = shape || groupContext.shape;
        const finalVariant = variant || groupContext.variant;
        const finalSize = size || groupContext.size;

        return tagStyles({
          ...renderProps,
          className: cn([
            badgeStyles({
              color: finalColor,
              shape: finalShape,
              variant: finalVariant,
              size: finalSize,
            }),
            renderProps.isSelected && finalColor
              ? badgeStyles.variants.color[finalColor]
              : undefined,
          ]),
        });
      })}
    >
      {({ allowsRemoving }) => {
        return (
          <>
            {children}
            {allowsRemoving && (
              <Button
                slot="remove"
                className={composeRenderProps("", (className) => {
                  return cn(
                    "rounded size-3.5 grid place-content-center -mr-0.5 text-muted-foreground hover:text-foreground outline-hidden",
                    className,
                  );
                })}
              >
                <span className="shrink-0 text-base/4 -mr-px">
                  <XIcon data-slot="icon" className="size-3" />
                </span>
              </Button>
            )}
          </>
        );
      }}
    </TagPrimitive>
  );
}
